import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  BookOpen, Award, Clock, ChevronRight, Download, Plus,
  Users, Trash2, X, CheckCircle, AlertCircle, LogOut,
  Building2, MapPin, Shield, UserPlus, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LINE_COLORS = {
  kids: 'from-purple-600 to-pink-600',
  teens: 'from-blue-600 to-cyan-600',
  adultos: 'from-emerald-600 to-teal-600',
  profesionales: 'from-amber-600 to-orange-600',
  social: 'from-rose-600 to-pink-700',
};

// ─── Cohorte Tab ─────────────────────────────────────────────────────────────
function CohorteTab({ authFetch }) {
  const [membership, setMembership] = useState(undefined); // undefined=loading, null=none, obj=member
  const [cohorte, setCohorte] = useState(null);
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [message, setMessage] = useState(null); // {type:'ok'|'err', text}

  useEffect(() => {
    authFetch(`${API}/academy/my-cohorte`).then(r => r.json())
      .then(d => { setMembership(d.membership); setCohorte(d.cohorte); })
      .catch(() => setMembership(null));
  }, [authFetch]);

  const join = async () => {
    if (!code.trim()) return;
    setJoining(true); setMessage(null);
    try {
      const res = await authFetch(`${API}/academy/cohortes/join-by-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim().toUpperCase() })
      });
      const data = await res.json();
      if (data.success) {
        setMembership({ cohorte_code: code.toUpperCase(), joined_at: new Date().toISOString() });
        setCohorte(data.cohorte);
        setCode('');
        setMessage({ type: 'ok', text: '¡Te has unido a la cohorte exitosamente!' });
      } else { setMessage({ type: 'err', text: data.detail || 'Código inválido' }); }
    } catch (e) { setMessage({ type: 'err', text: 'Error al unirse. Verifica el código.' }); }
    setJoining(false);
  };

  const leave = async () => {
    if (!window.confirm('¿Salir de esta cohorte?')) return;
    setLeaving(true);
    try {
      await authFetch(`${API}/academy/my-cohorte`, { method: 'DELETE' });
      setMembership(null); setCohorte(null);
      setMessage({ type: 'ok', text: 'Has salido de la cohorte.' });
    } catch (e) { setMessage({ type: 'err', text: 'No se pudo salir de la cohorte.' }); }
    setLeaving(false);
  };

  if (membership === undefined) return (
    <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>
  );

  return (
    <div className="space-y-5" data-testid="cohorte-tab">
      {message && (
        <div className={`text-sm px-4 py-3 rounded-xl border ${message.type === 'ok' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {membership && cohorte ? (
        // Current membership card
        <div className="bg-gradient-to-br from-violet-900/20 to-indigo-900/20 border border-violet-500/30 rounded-2xl p-5 space-y-4" data-testid="cohorte-membership-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-violet-400" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-violet-400 font-bold uppercase tracking-widest mb-0.5">Cohorte activa · Mediadores del Futuro</p>
              <h3 className="text-white font-bold text-base">{cohorte.name}</h3>
              <p className="text-[#94A3B8] text-xs mt-0.5">{cohorte.organization}</p>
            </div>
            <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full border border-emerald-500/20 font-bold">Activa</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.08)] rounded-xl p-3 text-center">
              <p className="text-white font-black text-2xl">{cohorte.current_participants}</p>
              <p className="text-[#94A3B8] text-[10px]">participantes</p>
            </div>
            <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.08)] rounded-xl p-3 text-center">
              <p className="text-white font-black text-2xl">{cohorte.max_participants}</p>
              <p className="text-[#94A3B8] text-[10px]">plazas totales</p>
            </div>
          </div>

          <div className="space-y-2 text-xs text-[#94A3B8]">
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
              <span>{cohorte.city}, {cohorte.country} · {cohorte.start_date} → {cohorte.end_date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
              <span>Responsable: {cohorte.safeguard_email || 'Por confirmar'}</span>
            </div>
          </div>

          {cohorte.help_lines?.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 space-y-1.5">
              <p className="text-xs font-bold text-red-400">Líneas de apoyo de tu programa</p>
              {cohorte.help_lines.map((hl, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-[#94A3B8]">{hl.name}</span>
                  <a href={`tel:${hl.number}`} className="text-emerald-400 font-bold">{hl.number}</a>
                </div>
              ))}
            </div>
          )}

          <button onClick={leave} disabled={leaving} data-testid="leave-cohorte-btn"
            className="w-full py-2.5 rounded-xl border border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:text-red-400 hover:border-red-500/30 text-xs transition disabled:opacity-50">
            {leaving ? 'Saliendo...' : 'Salir de esta cohorte'}
          </button>
        </div>
      ) : (
        // Join form
        <div className="space-y-5">
          <div className="text-center py-6 space-y-2">
            <div className="w-16 h-16 rounded-2xl bg-violet-600/10 border border-violet-500/20 flex items-center justify-center mx-auto">
              <UserPlus className="w-8 h-8 text-violet-400/60" />
            </div>
            <h3 className="text-white font-bold">Únete a tu cohorte</h3>
            <p className="text-[#94A3B8] text-sm max-w-xs mx-auto">
              Tu institución o mentor te habrá dado un código de cohorte para acceder al programa grupal.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Código de cohorte</label>
            <input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && join()}
              placeholder="Ej: DEMUNA-LC, DONBOSCO-L..."
              data-testid="cohorte-code-input"
              className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-violet-500/50 rounded-xl px-4 py-3.5 text-white text-sm placeholder-[#94A3B8]/40 focus:outline-none transition font-mono tracking-widest"
            />
            <button onClick={join} disabled={!code.trim() || joining} data-testid="join-cohorte-btn"
              className="w-full py-3.5 rounded-2xl bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white font-bold transition flex items-center justify-center gap-2">
              {joining ? <><Loader2 className="w-4 h-4 animate-spin" /> Verificando...</> : <><UserPlus className="w-4 h-4" /> Unirme a la cohorte</>}
            </button>
          </div>

          {/* Demo codes hint */}
          <div className="bg-[rgba(148,163,184,0.04)] border border-[rgba(148,163,184,0.10)] rounded-xl p-4 space-y-2">
            <p className="text-xs font-bold text-[#94A3B8]">Códigos de cohorte disponibles en el piloto:</p>
            <div className="flex flex-wrap gap-2">
              {['DEMUNA-LC', 'DONBOSCO-L', 'INABIF-01', 'CEDRO-L'].map(c => (
                <button key={c} onClick={() => setCode(c)} data-testid={`hint-code-${c}`}
                  className="text-xs px-2.5 py-1 rounded-lg bg-violet-600/10 border border-violet-500/20 text-violet-400 hover:bg-violet-600/20 transition font-mono">
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add Child Modal ─────────────────────────────────────────────────────
function AddChildModal({ onClose, onAdded, authFetch }) {
  const [firstName, setFirstName] = useState('');
  const [ageRange, setAgeRange] = useState('5-6');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) { setError('Debes aceptar el consentimiento parental'); return; }
    setLoading(true); setError('');
    try {
      const res = await authFetch(`${API}/academy/children`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          age_range: ageRange,
          parental_consent: consent,
          consent_text: 'Acepto el tratamiento de los datos del menor conforme a la política de privacidad de Neuralia369 Academy'
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al crear el perfil');
      onAdded(data.profile);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="add-child-modal">
      <div className="bg-[rgba(8,15,32,0.98)] border border-[rgba(148,163,184,0.2)] rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white">Añadir perfil de menor</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white transition"><X size={18} /></button>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-300 mb-4">
          Los menores no tienen email ni contraseña. Su acceso lo gestiona el tutor familiar.
        </div>

        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-xs text-red-400 flex gap-2">
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-child-form">
          <div>
            <label className="block text-xs text-[#94A3B8] mb-1.5 font-medium">Nombre del menor</label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required
              data-testid="child-firstname-input"
              className="w-full bg-white/5 border border-[rgba(148,163,184,0.14)] rounded-xl px-4 py-2.5 text-white text-sm placeholder-[#94A3B8]/50 focus:outline-none focus:border-indigo-500 transition"
              placeholder="Nombre (solo nombre de pila)" />
          </div>
          <div>
            <label className="block text-xs text-[#94A3B8] mb-1.5 font-medium">Franja de edad</label>
            <select value={ageRange} onChange={e => setAgeRange(e.target.value)}
              data-testid="child-age-range-select"
              className="w-full bg-[rgba(8,15,32,0.9)] border border-[rgba(148,163,184,0.14)] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition">
              <option value="5-6">5-6 años (K0 Mini Exploradores)</option>
              <option value="7-9">7-9 años (K1 Detectives IA)</option>
              <option value="10-12">10-12 años (K2 Creadores IA)</option>
              <option value="13-17">13-17 años (T1 IA Responsable)</option>
            </select>
          </div>
          <div className="flex items-start gap-3">
            <input type="checkbox" id="consent" checked={consent} onChange={e => setConsent(e.target.checked)}
              data-testid="consent-checkbox"
              className="mt-0.5 accent-indigo-600" />
            <label htmlFor="consent" className="text-xs text-[#94A3B8] leading-relaxed">
              Acepto como tutor/a responsable que los datos del menor se tratarán según la política de privacidad de Neuralia369 Academy, exclusivamente para su formación.
            </label>
          </div>
          <button type="submit" disabled={loading || !firstName || !consent}
            data-testid="add-child-submit-btn"
            className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition disabled:opacity-40">
            {loading ? 'Creando perfil...' : 'Crear perfil de menor'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Certificate card ─────────────────────────────────────────────────────
function CertCard({ cert, authFetch }) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await authFetch(`${API}/academy/certificates/${cert.code}/download`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificado-${cert.code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { }
    finally { setDownloading(false); }
  };

  return (
    <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-5 flex items-center gap-4" data-testid={`cert-card-${cert.code}`}>
      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        <Award size={18} className="text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm truncate">{cert.course_name}</p>
        <p className="text-xs text-[#94A3B8] mt-0.5">{cert.issued_date?.slice(0, 10)} · {cert.hours}h</p>
        <p className="text-[10px] font-mono text-[#94A3B8]/60 mt-0.5">{cert.code}</p>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <Link to={`/academy/verificar/${cert.code}`}
          className="text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded-lg border border-indigo-500/20 hover:border-indigo-500/40 transition">
          Ver
        </Link>
        <button onClick={download} disabled={downloading} data-testid={`download-cert-${cert.code}`}
          className="text-xs text-white bg-indigo-600 hover:bg-indigo-500 px-3 py-1 rounded-lg transition disabled:opacity-50 flex items-center gap-1">
          <Download size={11} /> {downloading ? '...' : 'PDF'}
        </button>
      </div>
    </div>
  );
}

// ─── Enrollment card ─────────────────────────────────────────────────────
function EnrollCard({ enrollment }) {
  const c = enrollment.course;
  if (!c) return null;
  return (
    <Link to={`/academy/aula/${c.id || c.code}`} data-testid={`enrollment-card-${c.id}`}
      className="group bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl overflow-hidden hover:border-indigo-500/30 transition">
      <div className={`h-2 bg-gradient-to-r ${LINE_COLORS[c.line] || 'from-indigo-600 to-indigo-800'}`} style={{ width: `${enrollment.progress_pct || 0}%` }} />
      <div className="p-5 flex gap-4 items-center">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${LINE_COLORS[c.line] || 'from-indigo-700 to-indigo-900'} flex items-center justify-center flex-shrink-0`}>
          <BookOpen size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">{c.name}</p>
          <div className="flex items-center gap-3 mt-1">
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-1.5 bg-indigo-500 rounded-full" style={{ width: `${enrollment.progress_pct || 0}%` }} />
            </div>
            <span className="text-xs text-[#94A3B8] flex-shrink-0">{enrollment.progress_pct || 0}%</span>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-0.5">{c.weeks} semanas · {c.modules_count} módulos</p>
        </div>
        <ChevronRight size={15} className="text-[#94A3B8] group-hover:text-white transition flex-shrink-0" />
      </div>
    </Link>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────
export default function AcademyDashboardPage() {
  const { user, logout, authFetch, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [children, setChildren] = useState([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [generatingCert, setGeneratingCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('courses');

  const isTutor = user?.role === 'tutor_familia';
  const isAdmin = ['admin', 'admin_neuralia', 'admin_empresa', 'admin_waca'].includes(user?.role);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/acceso-academy'); return; }
    const load = async () => {
      try {
        const [eRes, cRes] = await Promise.all([
          authFetch(`${API}/academy/my-courses`),
          authFetch(`${API}/academy/my-certificates`)
        ]);
        const eData = await eRes.json();
        const cData = await cRes.json();
        setEnrollments(eData.enrollments || []);
        setCerts(cData.certificates || []);
        if (isTutor) {
          const chRes = await authFetch(`${API}/academy/children`);
          const chData = await chRes.json();
          setChildren(chData.children || []);
        }
      } catch (e) { }
      finally { setLoading(false); }
    };
    load();
  }, [user, authLoading, navigate, authFetch, isTutor]);

  const handleGenerateCert = async (enrollment) => {
    setGeneratingCert(enrollment.course_id);
    try {
      const res = await authFetch(`${API}/academy/certificates/generate/${enrollment.course_id}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setCerts(p => {
          const exists = p.find(c => c.code === data.certificate.code);
          return exists ? p : [...p, data.certificate];
        });
        setActiveTab('certificates');
      }
    } catch (e) { }
    finally { setGeneratingCert(null); }
  };

  const handleDeleteChild = async (childId) => {
    if (!window.confirm('¿Eliminar este perfil de menor? Se borrarán todos sus datos (RGPD).')) return;
    try {
      await authFetch(`${API}/academy/children/${childId}`, { method: 'DELETE' });
      setChildren(p => p.filter(c => c.id !== childId));
    } catch (e) { }
  };

  if (!user) return null;
  if (loading) return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const completedEnrollments = enrollments.filter(e => e.progress_pct === 100);
  const inProgressEnrollments = enrollments.filter(e => e.progress_pct < 100);

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="academy-dashboard">
      {/* Header */}
      <div className="bg-[rgba(8,15,32,0.95)] border-b border-[rgba(148,163,184,0.14)] px-4 py-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/academy" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center">
              <BookOpen size={14} className="text-white" />
            </div>
            <span className="text-sm font-bold text-white hidden sm:block">Academy</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-[#94A3B8]">Bienvenido/a</p>
              <p className="text-sm font-bold text-white truncate max-w-[160px]">{user.nombre || user.email}</p>
            </div>
            <button onClick={logout} className="text-[#94A3B8] hover:text-white transition" title="Cerrar sesión">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Cursos inscritos', value: enrollments.length, color: 'text-indigo-400' },
            { label: 'Completados', value: completedEnrollments.length, color: 'text-emerald-400' },
            { label: 'Certificados', value: certs.length, color: 'text-amber-400' },
          ].map(s => (
            <div key={s.label} className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-4 text-center">
              <p className={`text-3xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-[#94A3B8] mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-xl p-1">
          {[
            { id: 'courses', label: 'Mis Cursos' },
            { id: 'certificates', label: `Certificados (${certs.length})` },
            { id: 'cohorte', label: 'Mi Cohorte' },
            ...(isTutor ? [{ id: 'children', label: `Menores (${children.length})` }] : []),
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'text-[#94A3B8] hover:text-white'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Courses tab */}
        {activeTab === 'courses' && (
          <div className="space-y-6">
            {inProgressEnrollments.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider mb-3">En progreso</h3>
                <div className="space-y-3">
                  {inProgressEnrollments.map(e => <EnrollCard key={e.course_id} enrollment={e} />)}
                </div>
              </div>
            )}
            {completedEnrollments.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider mb-3">Completados</h3>
                <div className="space-y-3">
                  {completedEnrollments.map(e => (
                    <div key={e.course_id} className="space-y-2">
                      <EnrollCard enrollment={e} />
                      {!certs.find(c => c.course_id === e.course_id) && (
                        <button onClick={() => handleGenerateCert(e)}
                          disabled={generatingCert === e.course_id}
                          data-testid={`generate-cert-${e.course_id}`}
                          className="w-full py-2 rounded-xl border border-amber-500/40 text-amber-400 text-xs font-medium hover:bg-amber-500/10 transition disabled:opacity-50 flex items-center justify-center gap-2">
                          <Award size={13} />
                          {generatingCert === e.course_id ? 'Generando...' : 'Generar certificado'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {enrollments.length === 0 && (
              <div className="text-center py-16 space-y-4">
                <BookOpen size={40} className="text-[#94A3B8]/30 mx-auto" />
                <p className="text-[#94A3B8]">Aún no tienes cursos inscritos</p>
                <Link to="/academy" data-testid="browse-courses-btn"
                  className="inline-block px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold transition">
                  Explorar catálogo
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Certificates tab */}
        {activeTab === 'certificates' && (
          <div className="space-y-3">
            {certs.length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <Award size={40} className="text-[#94A3B8]/30 mx-auto" />
                <p className="text-[#94A3B8]">Completa un curso para obtener tu primer certificado</p>
              </div>
            ) : (
              certs.map(c => <CertCard key={c.code} cert={c} authFetch={authFetch} />)
            )}
          </div>
        )}

        {/* Cohorte tab */}
        {activeTab === 'cohorte' && <CohorteTab authFetch={authFetch} />}

        {/* Children tab (tutors only) */}
        {activeTab === 'children' && isTutor && (          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#94A3B8]">Gestiona los perfiles de tus hijos</p>
              <button onClick={() => setShowAddChild(true)} data-testid="add-child-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition">
                <Plus size={13} /> Añadir menor
              </button>
            </div>
            {children.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <Users size={36} className="text-[#94A3B8]/30 mx-auto" />
                <p className="text-[#94A3B8] text-sm">Aún no tienes perfiles de menores</p>
                <button onClick={() => setShowAddChild(true)} className="text-indigo-400 hover:text-indigo-300 text-sm">
                  + Crear el primero
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {children.map(child => (
                  <div key={child.id} className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-4 flex items-center gap-4" data-testid={`child-card-${child.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-purple-600/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">
                        {child.age_range === '5-6' ? '🐣' : child.age_range === '7-9' ? '🔍' : child.age_range === '10-12' ? '🎨' : '🌱'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold text-sm">{child.first_name}</p>
                      <p className="text-xs text-[#94A3B8]">{child.age_range} años · Cuenta gestionada por tutor</p>
                    </div>
                    <div className="flex gap-2">
                      <Link to="/academy" className="text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded-lg border border-indigo-500/20 transition">
                        Inscribir
                      </Link>
                      <button onClick={() => handleDeleteChild(child.id)} data-testid={`delete-child-${child.id}`}
                        className="text-[#94A3B8] hover:text-red-400 p-1 transition">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showAddChild && (
        <AddChildModal
          onClose={() => setShowAddChild(false)}
          onAdded={(profile) => { setChildren(p => [...p, profile]); setShowAddChild(false); }}
          authFetch={authFetch}
        />
      )}
    </div>
  );
}
